import React from 'react';
import Select from 'react-select';

import style from './UserDetail.module.css';
import usersStyle from './Users.module.css';
import UserTable from './UserTable';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import { getSession } from 'Vulture/Session';
import { broncoURL } from 'Vulture/ENV.js';
import { hasPermission, isInitialized } from 'Vulture/Permissions.js';

// Email format validation, mirrored from the backend (controller/user_management.go
// isValidEmail): a local part, "@", and a domain of dot-separated labels (each starting
// and ending alphanumeric — no leading/trailing hyphen) ending in a 2+ letter TLD.
// Client-side check gives immediate feedback; the backend still enforces it.
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

// Unified User Edit/Create page. Lists users (scoped by the backend), and on selecting
// or creating one shows a per-account-card form (role + properties + folders +
// slideshows) backed by the /api/user* endpoints. Gating is enforced server-side; the
// UI mirrors it via the granular permissions for a clean experience.
const prettyRole = (name) => (name || '')
	.split('_')
	.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
	.join(' ');

// can() defaults to allow when the permission cache isn't loaded yet — the backend is
// the real gate, so we never hard-hide on an unloaded cache.
const can = (perm) => !isInitialized() || hasPermission(perm);

const blankUser = () => ({
	uuid:       '',
	email:      '',
	first_name: '',
	last_name:  '',
	title:      '',
	active:     true,
	has_2fa:    true,
	accounts:   [],
});

class UserAdmin extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			view:              'list', // 'list' | 'detail'
			loading:           false,
			error:             null,
			notice:            null,

			users:             [],
			filter:            '',
			// Account pre-filter: set when navigated with an account context (sidebar
			// "Users" inside an account → /userAdmin/{account}); the More Tools "Admin
			// Users" entry navigates without one (full list).
			accountFilter:     props.filterAccountUUID || '',

			roles:             [],
			rolesLoaded:       false,
			availableAccounts: [],

			detail:            null,
			isNew:             false,
			saving:            false,
			expandedAccounts:  {},
			propertySearch:    {},
			showAccountPicker: false,
			deleteConfirm:     false,
		};
		this.searchTimer = null;
	}

	componentDidMount() {
		this.loadRoles();
		this.loadAvailableAccounts();
		this.search('');
	}

	componentDidUpdate(prevProps) {
		if (prevProps.filterAccountUUID !== this.props.filterAccountUUID) {
			this.setState({
				accountFilter: this.props.filterAccountUUID || '',
			}, () => this.search(this.state.filter));
		}
	}

	apiRequest(path, opts = {}) {
		const session = getSession();
		const defaults = {
			headers: {
				'Content-Type':   'application/json',
				'X-Auth-Session': session.session,
			},
		};
		return fetch(`${broncoURL}${path}`, { ...defaults, ...opts }).then((res) => {
			if (!res.ok) {
				return res.json().then((d) => { throw new Error(d.error || res.statusText); });
			}
			return res.json();
		});
	}

	loadRoles() {
		this.apiRequest('/api/roles', { method: 'GET' })
			.then((data) => this.setState({ roles: data.roles || [], rolesLoaded: true }))
			.catch((err) => this.setState({ error: err.message, rolesLoaded: true }));
	}

	loadAvailableAccounts() {
		// Accounts the admin may ASSIGN (all accounts for a global admin, else the
		// admin's accessible accounts) — NOT /accounts/list, which returns the session
		// user's own memberships and is empty for a global admin.
		this.apiRequest('/api/users/accounts', { method: 'GET' })
			.then((data) => this.setState({ availableAccounts: data.accounts || [] }))
			.catch(() => {});
	}

	roleOptions() {
		return this.state.roles.map((r) => ({ value: r.uuid, label: prettyRole(r.display_name) }));
	}

	// Only roles the caller could actually assign (backend-computed privilege ceiling —
	// same MissingToGrant check the save path enforces). Used for the picker OPTIONS;
	// the current value still resolves via roleOptions() so an existing higher role
	// remains visible on users who already hold it.
	//
	// Scoped to accountUUID: custom (account-bound) roles only belong to their own
	// account; system/default roles (no account_uuid) are assignable everywhere. The
	// save path enforces this too (validateSaveScope) — this is the UX half.
	grantableRoleOptions(accountUUID) {
		return this.state.roles
			.filter((r) => r.grantable !== false)
			.filter((r) => !r.account_uuid || r.account_uuid === accountUUID)
			.map((r) => ({ value: r.uuid, label: prettyRole(r.display_name) }));
	}

	// roleScopesFor returns ALL data-access scope dimensions of a role
	// (property/slideshow/folder are combinable; global/account stand alone). Drives which
	// per-account relation pickers show — a role scoped in several dimensions surfaces a
	// picker for each. Falls back to the single default_scope_type for older responses.
	// [] = no role selected.
	roleScopesFor(roleUUID) {
		if (!roleUUID) return [];
		const r = this.state.roles.find((x) => x.uuid === roleUUID);
		if (!r) return [];
		if (Array.isArray(r.scope_types) && r.scope_types.length) return r.scope_types;
		return r.default_scope_type ? [r.default_scope_type] : [];
	}

	// ---- List view ----
	// Column defs mirror the admin Users table (5 columns) so Users.module.css's
	// per-position widths (.th:nth-child(1..5)) line up. Email is the default sort key.
	checkboxCell(on) {
		return on ? (
			<div className={usersStyle.activeCheckbox}><div className={usersStyle.checkboxIcon}><CustomIcon icon="check" /></div></div>
		) : (
			<div className={usersStyle.disabledSquare}><div className={usersStyle.checkboxIcon}><CustomIcon icon="check" /></div></div>
		);
	}

	userColumns() {
		return [
			{ Header: 'Active', accessor: 'active', Cell: (row) => this.checkboxCell(row.value === true) },
			{ Header: '2FA', id: 'has_2fa', accessor: 'has_2fa', Cell: (row) => this.checkboxCell(row.value === true) },
			{ Header: 'Email', accessor: 'email' },
			{
				Header:   'Accounts',
				id:       'accounts',
				accessor: (r) => r.accounts || [],
				Cell:     (row) => (
					<div>
						{(row.value || []).map((name, i) => (
							<React.Fragment key={i}>
								<div className={usersStyle.propertyIconContainer}><CustomIcon icon="dot-circle" color="#00BAFF" /></div>
								<span>{name}</span>
							</React.Fragment>
						))}
					</div>
				),
			},
			{ Header: 'Role', id: 'roles', accessor: (r) => (r.roles || []).map(prettyRole).join(', ') },
		];
	}

	search(filter) {
		this.setState({ loading: true, filter: filter || '' });
		let query = `filter=${encodeURIComponent(filter || '')}`;
		if (this.state.accountFilter) {
			query += `&account_uuid=${encodeURIComponent(this.state.accountFilter)}`;
		}
		this.apiRequest(`/api/users?${query}`, { method: 'GET' })
			.then((data) => this.setState({ users: data.users || [], loading: false }))
			.catch((err) => this.setState({ error: err.message, loading: false }));
	}

	openUser(uuid) {
		this.setState({ loading: true, error: null, notice: null });
		this.apiRequest(`/api/user/${uuid}`, { method: 'GET' })
			.then((u) => this.setState({
				detail:            this.normalizeDetail(u),
				isNew:             false,
				view:             'detail',
				loading:           false,
				expandedAccounts:  {},
				propertySearch:    {},
				showAccountPicker: false,
				deleteConfirm:     false,
			}))
			.catch((err) => this.setState({ error: err.message, loading: false }));
	}

	newUser() {
		this.setState({
			detail:            blankUser(),
			isNew:             true,
			view:             'detail',
			error:             null,
			notice:            null,
			expandedAccounts:  {},
			propertySearch:    {},
			showAccountPicker: false,
			deleteConfirm:     false,
		});
	}

	backToList() {
		this.setState({ view: 'list', detail: null });
		this.search('');
	}

	// API responses already match this shape; clone so edits are local until save.
	normalizeDetail(u) {
		return JSON.parse(JSON.stringify({
			uuid:       u.uuid,
			email:      u.email || '',
			first_name: u.first_name || '',
			last_name:  u.last_name || '',
			title:      u.title || '',
			active:     !!u.active,
			has_2fa:    !!u.has_2fa,
			accounts:   u.accounts || [],
		}));
	}

	// ---- Detail edits ----
	setField(key, value) {
		this.setState((prev) => ({ detail: { ...prev.detail, [key]: value } }));
	}

	updateAccount(accountUUID, mutate) {
		this.setState((prev) => ({
			detail: {
				...prev.detail,
				accounts: prev.detail.accounts.map((a) => (a.account_uuid === accountUUID ? mutate({ ...a }) : a)),
			},
		}));
	}

	setRole(accountUUID, roleUUID) {
		this.updateAccount(accountUUID, (a) => {
			a.permission_group_uuid = roleUUID;
			a.role_inherited = false;
			return a;
		});
	}

	toggleProperty(accountUUID, sgUUID) {
		this.updateAccount(accountUUID, (a) => {
			a.properties = a.properties.map((p) => (p.uuid === sgUUID ? { ...p, assigned: !p.assigned } : p));
			return a;
		});
	}

	selectAllProperties(accountUUID, val) {
		this.updateAccount(accountUUID, (a) => {
			a.properties = a.properties.map((p) => ({ ...p, assigned: val }));
			return a;
		});
	}

	toggleFolder(accountUUID, id) {
		this.updateAccount(accountUUID, (a) => {
			a.folders = a.folders.map((f) => (f.id === id ? { ...f, assigned: !f.assigned } : f));
			return a;
		});
	}

	toggleSlideshow(accountUUID, name) {
		this.updateAccount(accountUUID, (a) => {
			a.slideshows = a.slideshows.map((s) => (s.name === name ? { ...s, assigned: !s.assigned } : s));
			return a;
		});
	}

	addAccount(accountUUID) {
		if (this.state.detail.accounts.some((a) => a.account_uuid === accountUUID)) return;
		this.apiRequest(`/api/account/${accountUUID}/assignable`, { method: 'GET' })
			.then((card) => this.setState((prev) => ({
				showAccountPicker: false,
				expandedAccounts:  { ...prev.expandedAccounts, [accountUUID]: true },
				detail:            { ...prev.detail, accounts: [...prev.detail.accounts, card] },
			})))
			.catch((err) => this.setState({ error: err.message }));
	}

	removeAccount(accountUUID) {
		this.setState((prev) => ({
			detail: { ...prev.detail, accounts: prev.detail.accounts.filter((a) => a.account_uuid !== accountUUID) },
		}));
	}

	toggleExpand(accountUUID) {
		this.setState((prev) => ({
			expandedAccounts: { ...prev.expandedAccounts, [accountUUID]: !prev.expandedAccounts[accountUUID] },
		}));
	}

	buildPayload() {
		const d = this.state.detail;
		return {
			email:      d.email,
			first_name: d.first_name,
			last_name:  d.last_name,
			title:      d.title,
			active:     d.active,
			has_2fa:    d.has_2fa,
			accounts:   d.accounts.map((a) => {
				// Only send the relations the role's scope actually uses — so changing a
				// role (e.g. property_manager → content_manager) clears the now-irrelevant
				// assignments instead of leaving stale ones the hidden picker can't manage.
				const scopes = this.roleScopesFor(a.permission_group_uuid);
				return {
					account_uuid:          a.account_uuid,
					permission_group_uuid: a.permission_group_uuid || '',
					properties:            scopes.includes('property')  ? (a.properties || []).filter((p) => p.assigned).map((p) => p.uuid) : [],
					folders:               scopes.includes('folder')    ? (a.folders || []).filter((f) => f.assigned).map((f) => f.id)      : [],
					slideshows:            scopes.includes('slideshow') ? (a.slideshows || []).filter((s) => s.assigned).map((s) => s.name) : [],
				};
			}),
		};
	}

	save() {
		const d = this.state.detail;
		if (!d.email) { this.setState({ error: 'Email is required' }); return; }
		if (!EMAIL_RE.test(d.email)) { this.setState({ error: 'invalid email address' }); return; }
		if (this.state.isNew && !d.accounts.some((a) => a.permission_group_uuid)) {
			this.setState({ error: 'Assign at least one account with a role so the user can sign in' });
			return;
		}
		// Mirror the backend's "every account card must carry a role" rule client-side so
		// the message can name the account (the server only knows the UUID cheaply).
		const roleless = d.accounts.find((a) => !a.permission_group_uuid);
		if (roleless) {
			const label = roleless.account_name || roleless.account_uuid;
			this.setState({ error: `Assign a role for "${label}" (or remove the account)` });
			return;
		}

		this.setState({ saving: true, error: null, notice: null });
		const payload = this.buildPayload();
		const path = this.state.isNew ? '/api/user' : `/api/user/${d.uuid}`;
		this.apiRequest(path, { method: 'POST', body: JSON.stringify(payload) })
			.then((resp) => {
				if (this.state.isNew) {
					this.setState({ saving: false });
					this.backToList();
				} else {
					this.setState({ saving: false, detail: this.normalizeDetail(resp), notice: 'Saved' });
				}
			})
			.catch((err) => this.setState({ saving: false, error: err.message }));
	}

	deleteUser() {
		const d = this.state.detail;
		this.setState({ saving: true, error: null });
		this.apiRequest(`/api/user/${d.uuid}`, { method: 'DELETE' })
			.then(() => { this.setState({ saving: false }); this.backToList(); })
			.catch((err) => this.setState({ saving: false, error: err.message, deleteConfirm: false }));
	}

	// Resend the setup / password-reset email via the shared /password/forgot endpoint.
	// Covers both "user lost their setup email" and "force a password reset".
	resetPassword() {
		const d = this.state.detail;
		if (!d.email) { this.setState({ error: 'User has no email' }); return; }
		this.setState({ saving: true, error: null, notice: null });
		this.apiRequest('/password/forgot', { method: 'POST', body: JSON.stringify({ email: d.email }) })
			.then(() => this.setState({ saving: false, notice: 'Password reset email sent' }))
			.catch((err) => this.setState({ saving: false, error: err.message }));
	}

	// ---- Render ----
	render() {
		if (isInitialized() && !hasPermission('user_view')) {
			return <div className={style.container}><div className={style.emptyState}>You do not have permission to manage users.</div></div>;
		}

		// List view uses the admin table's page layout (systems-page); detail view uses
		// the UserDetail form container.
		if (this.state.view === 'list') {
			return (
				<div className="systems-page">
					{this.state.error && <div className={style.errorBanner}>{this.state.error}</div>}
					{this.renderList()}
				</div>
			);
		}
		return (
			<div className={style.container}>
				<div className={style.inner}>
					{this.state.error && <div className={style.errorBanner} data-test-id="userError">{this.state.error}</div>}
					{this.state.notice && <div className={style.successBanner} data-test-id="userNotice">{this.state.notice}</div>}
					{this.renderDetail()}
				</div>
			</div>
		);
	}

	renderList() {
		const filterAccount = this.state.accountFilter
			? this.state.availableAccounts.find((a) => a.uuid === this.state.accountFilter)
			: null;

		return (
			<React.Fragment>
				<div className="settings-header">
					<h2>Users</h2>
					{this.state.accountFilter && (
						<div className={style.accountFilterChip} data-test-id="accountFilterChip">
							<CustomIcon icon="filter" />
							<span>{filterAccount ? filterAccount.display_name : 'Selected account'}</span>
							<button
								type="button"
								data-test-id="clearAccountFilter"
								title="Show all users"
								onClick={() => this.props.app.redirect({ to: '/userAdmin' })}
							>×</button>
						</div>
					)}
				</div>
				<UserTable
					data={this.state.users}
					columns={this.userColumns()}
					isAccountUser={false}
					handleRootUserReadRequest={(value) => this.search(value)}
					openModal={(row) => {
						if (row && row.original && row.original.uuid) {
							this.openUser(row.original.uuid);
						} else {
							this.newUser();
						}
					}}
				/>
			</React.Fragment>
		);
	}

	renderDetail() {
		const d = this.state.detail;
		if (!d) return null;
		const canSave = this.state.isNew ? can('user_create') : can('user_edit');
		const availableOptions = this.state.availableAccounts
			.filter((a) => !d.accounts.some((acc) => acc.account_uuid === a.uuid))
			.map((a) => ({ value: a.uuid, label: a.display_name }));

		return (
			<div>
				<div className={style.backLink} onClick={() => this.backToList()}>&#8592; Back to users</div>
				<div className={style.pageHeader} data-test-id="userDetailHeader">{this.state.isNew ? 'New User' : d.email}</div>

				{/* Basic Information */}
				<div className={style.section}>
					<div className={style.sectionTitle}>Basic Information</div>
					<div className={style.fieldGrid}>
						<div className={style.formField}>
							<label>Email</label>
							<input type="text" placeholder="Email address" value={d.email} data-test-id="emailAddressInput"
								onChange={(e) => this.setField('email', e.target.value)} />
						</div>
						<div className={style.formField}>
							<label>First Name</label>
							<input type="text" placeholder="First Name" value={d.first_name} data-test-id="firstNameInput"
								onChange={(e) => this.setField('first_name', e.target.value)} />
						</div>
						<div className={style.formField}>
							<label>Last Name</label>
							<input type="text" placeholder="Last Name" value={d.last_name} data-test-id="lastNameInput"
								onChange={(e) => this.setField('last_name', e.target.value)} />
						</div>
						<div className={style.formField}>
							<label>Title</label>
							<input type="text" placeholder="Title" value={d.title} data-test-id="titleInput"
								onChange={(e) => this.setField('title', e.target.value)} />
						</div>
					</div>
					<div className={style.toggleRow} style={{ marginTop: '16px' }}>
						<div className={style.toggleItem}>
							<label>Active</label>
							<input className="on-off on-off-label" type="checkbox" checked={d.active} data-test-id="activeToggle"
								onChange={() => this.setField('active', !d.active)} />
						</div>
						<div className={style.toggleItem}>
							<label>2FA Enabled</label>
							<input className="on-off on-off-label" type="checkbox" checked={d.has_2fa} data-test-id="twoFAToggle"
								onChange={() => this.setField('has_2fa', !d.has_2fa)} />
						</div>
					</div>
				</div>

				{/* Access Scoping (role lives in each account card) */}
				<div className={style.section}>
					<div className={style.sectionTitle}>Access Scoping</div>
					<div className={style.accountsHeader}>
						<span data-test-id="assignedAccountCount">{d.accounts.length} account(s) assigned</span>
						{!this.state.showAccountPicker ? (
							<button className={style.addAccountBtn} data-test-id="addAccountButton" onClick={() => this.setState({ showAccountPicker: true })}>+ Add Account</button>
						) : (
							<button className={style.secondaryBtn} data-test-id="cancelAddAccountButton" onClick={() => this.setState({ showAccountPicker: false })}>Cancel</button>
						)}
					</div>
					{this.state.showAccountPicker && (
						<div className={style.accountPicker} data-test-id="accountPicker">
							<Select
								options={availableOptions}
								placeholder="Search and select an account..."
								onChange={(e) => { if (e) this.addAccount(e.value); }}
								isClearable
							/>
						</div>
					)}
					{d.accounts.map((a) => this.renderAccountCard(a))}
					{d.accounts.length === 0 && (
						<div className={style.emptyState}>No accounts assigned. Click "+ Add Account" to assign access.</div>
					)}
				</div>

				{/* Actions */}
				<div className={style.actionBar}>
					<div className={style.actionBarLeft}>
						{!this.state.isNew && can('user_delete') && (
							this.state.deleteConfirm ? (
								<span className={style.deleteConfirm}>
									Delete this user?
									<span className={style.deleteConfirmActions}>
										<button className={style.dangerBtn} data-test-id="confirmDeleteUserButton" disabled={this.state.saving} onClick={() => this.deleteUser()}>Yes, delete</button>
										<button className={style.secondaryBtn} onClick={() => this.setState({ deleteConfirm: false })}>Cancel</button>
									</span>
								</span>
							) : (
								<button className={style.dangerBtn} data-test-id="deleteUserButton" onClick={() => this.setState({ deleteConfirm: true })}>Delete User</button>
							)
						)}
						{!this.state.isNew && can('user_edit') && (
							<button className={style.secondaryBtn} data-test-id="resetPasswordButton" disabled={this.state.saving} onClick={() => this.resetPassword()}>Reset Password</button>
						)}
					</div>
					<div className={style.actionBarRight}>
						<button className={style.secondaryBtn} data-test-id="cancelUserButton" onClick={() => this.backToList()}>Cancel</button>
						{canSave && (
							<button className={style.saveBtn} data-test-id="saveUserButton" disabled={this.state.saving} onClick={() => this.save()}>
								{this.state.saving ? 'Saving...' : (this.state.isNew ? 'Create User' : 'Save')}
							</button>
						)}
					</div>
				</div>
			</div>
		);
	}

	renderAccountCard(a) {
		const accountUUID = a.account_uuid;
		const expanded = !!this.state.expandedAccounts[accountUUID];
		const search = (this.state.propertySearch[accountUUID] || '').toLowerCase();
		const props = (a.properties || []);
		const selectedProps = props.filter((p) => p.assigned).length;
		const filteredProps = props.filter((p) => !search || (p.display_name || '').toLowerCase().includes(search));
		const selectedRole = this.roleOptions().find((o) => o.value === a.permission_group_uuid) || null;
		const roleScopes = this.roleScopesFor(a.permission_group_uuid);

		return (
			<div key={accountUUID} className={style.accountCard} data-test-id="accountCard" data-account-uuid={accountUUID} data-account-name={a.account_name}>
				<div className={style.cardHeader} data-test-id="accountCardHeader" onClick={() => this.toggleExpand(accountUUID)}>
					<div className={style.cardHeaderLeft}>
						<span className={`${style.cardExpander} ${expanded ? style.cardExpanderOpen : ''}`}>&#9654;</span>
						<span>{a.account_name}</span>
					</div>
					<button className={style.removeAccountBtn} data-test-id="removeAccountButton" onClick={(e) => { e.stopPropagation(); this.removeAccount(accountUUID); }}>Remove</button>
				</div>

				{expanded && (
					<div className={style.cardBody} data-test-id="accountCardBody">
						{/* Per-account role */}
						<div className={style.formField} style={{ maxWidth: '400px' }} data-test-id="roleSelect">
							<label>Role{a.role_inherited ? ' (inherited — set to make account-specific)' : ''}</label>
							<Select
								options={this.grantableRoleOptions(accountUUID)}
								value={selectedRole}
								placeholder="Select a role..."
								onChange={(e) => this.setRole(accountUUID, e ? e.value : '')}
								isClearable
								menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
								menuPosition="fixed"
								styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
							/>
						</div>

						{/* Scope hint for roles with no per-account sub-selection. */}
						{roleScopes.includes('account') && (
							<div style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>Account-wide access — no per-property selection.</div>
						)}
						{roleScopes.includes('global') && (
							<div style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>Full internal access — no per-account selection.</div>
						)}

						{/* Properties — only for property-scoped roles. */}
						{roleScopes.includes('property') && (
						<div className={style.scopeSection} data-test-id="propertiesSection">
							<div className={style.scopeSectionHeader}>Properties ({selectedProps}/{props.length})</div>
							<div className={style.selectButtons}>
								<button className={style.selectBtn} data-test-id="selectAllProperties" onClick={() => this.selectAllProperties(accountUUID, true)}>All</button>
								<button className={style.selectBtn} data-test-id="selectNoProperties" onClick={() => this.selectAllProperties(accountUUID, false)}>None</button>
							</div>
							<input
								className={style.propertySearch}
								type="text"
								placeholder="Search properties..."
								data-test-id="propertySearch"
								value={this.state.propertySearch[accountUUID] || ''}
								onChange={(e) => this.setState((prev) => ({ propertySearch: { ...prev.propertySearch, [accountUUID]: e.target.value } }))}
							/>
							<div className={style.propertyList}>
								{filteredProps.length > 0 ? filteredProps.map((p) => (
									<label key={p.uuid} className={style.propertyItem} data-test-id="propertyItem" data-property-name={p.display_name}>
										<input type="checkbox" checked={!!p.assigned} onChange={() => this.toggleProperty(accountUUID, p.uuid)} />
										<span>{p.display_name}</span>
									</label>
								)) : <div className={style.emptyState}>No properties found</div>}
							</div>
						</div>
						)}

						{/* Slideshows — content (slideshow-scoped) roles. */}
						{roleScopes.includes('slideshow') && (
							<div className={style.scopeSection} data-test-id="slideshowsSection">
								<div className={style.scopeSectionHeader}>Slideshows ({(a.slideshows || []).filter((s) => s.assigned).length}/{(a.slideshows || []).length})</div>
								{(a.slideshows || []).length > 0 ? (
									<div className={style.slideshowList}>
										{a.slideshows.map((s) => (
											<label key={s.name} className={style.slideshowItem} data-test-id="slideshowItem" data-slideshow-name={s.name}>
												<input type="checkbox" checked={!!s.assigned} onChange={() => this.toggleSlideshow(accountUUID, s.name)} />
												<span>{s.name}</span>
											</label>
										))}
									</div>
								) : <div className={style.emptyState}>No slideshows in this account</div>}
							</div>
						)}

						{/* Folders — content (slideshow) and folder-scoped roles. */}
						{roleScopes.includes('folder') && (
							<div className={style.scopeSection} data-test-id="foldersSection">
								<div className={style.scopeSectionHeader}>Folders ({(a.folders || []).filter((f) => f.assigned).length}/{(a.folders || []).length})</div>
								{(a.folders || []).length > 0 ? (
									<div className={style.folderTree}>
										{a.folders.map((f) => (
											<label key={f.id} className={style.folderItem} data-test-id="folderItem" data-folder-label={f.label} style={{ paddingLeft: f.parent_id ? '20px' : '0' }}>
												<input type="checkbox" checked={!!f.assigned} onChange={() => this.toggleFolder(accountUUID, f.id)} />
												<span className={style.folderLabel}>{f.label}</span>
											</label>
										))}
									</div>
								) : <div className={style.emptyState}>No folders in this account</div>}
							</div>
						)}

					</div>
				)}
			</div>
		);
	}
}

export default UserAdmin;
